import update from 'immutability-helper';
import { concat, get, isEqual, keyBy, merge, uniqWith, values } from 'lodash';
import { Reducer } from 'redux';
import {
    DETAILS_TAB,
    PAYMENT_PLANS_PAGE,
} from '../../config/tableAndPageConstants';
import {
    invoicesSortByOptions,
    invoicesStateFilterOptions,
} from '../../constants/invoicesSortAndFilters';
import {
    conversationTableFilterOptions,
    paymentPlansSortByOptions,
    paymentScheduleSortByOptions,
} from '../../constants/paymentPlansSortAndFilters';
import { PaymentPlansActionTypes, PaymentPlansState } from './types';

// Type-safe initialState!
export const initialState: PaymentPlansState = {
    loading: false,
    savePaymentPlanDataLoading: false,
    errorMessages: [],
    data: [],
    pageData: {
        pageSize: PAYMENT_PLANS_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    filters: {},
    tableFilter: undefined,
    sortBy: paymentPlansSortByOptions[1].value, //1 for created date
    sortAscending: false, // Descending order by default
    activeData: {
        record: {},
        invoices: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.INVOICE_LIST_COMPLETE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {
                InvoiceNumber: '',
                Customer: '',
                CustomerCode: '',
                CreatedDateMin: '',
                CreatedDateMax: '',
                DueDateMin: '',
                DueDateMax: '',
                AmountType: '',
                AmountValue: '',
                AmountOperator: '',
            },
            invoiceState: get(invoicesStateFilterOptions, `${1}.value`),
            sortBy: invoicesSortByOptions[0].value, // 2 for Invoice number
            sortAscending: true,
        },
        schedule: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.PAYMENT_PLAN_SCHEDULE_LIST.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            sortBy: paymentScheduleSortByOptions[0].value, // 2 for Invoice number
            sortAscending: true,
        },
        conversation: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            conversationTableFilter: get(
                conversationTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
        loading: false,
        selectedId: null,
        errorMessages: [],
    },
};

// Thanks to Redux 4's much simpler typings, we can take away a lot of typings on the reducer side,
// everything will remain type-safe.
const reducer: Reducer<PaymentPlansState> = (state = initialState, action) => {
    switch (action.type) {
        case PaymentPlansActionTypes.GET_PAYMENT_PLANS_REQUEST: {
            return { ...state, loading: true };
        }
        case PaymentPlansActionTypes.GET_PAYMENT_PLANS_SUCCESS: {
            let newDataState = [];
            if (action.payload.mergeData === true) {
                newDataState = values(
                    merge(
                        keyBy(state.data, 'Id'),
                        keyBy(action.payload.data, 'Id')
                    )
                );
            } else {
                if (action.payload.pageData.currentPage === 0) {
                    newDataState = update(state.data, {
                        $set: action.payload.data,
                    });
                } else {
                    newDataState = uniqWith(
                        concat(state.data, action.payload.data),
                        isEqual
                    );
                }
            }

            return {
                ...state,
                loading: false,
                data: newDataState,
                pageData: action.payload.pageData,
                errorMessages: initialState.errorMessages,
            };
        }
        case PaymentPlansActionTypes.GET_PAYMENT_PLANS_ERROR: {
            return {
                ...state,
                loading: false,
                data: initialState.data,
                errorMessages: action.payload,
            };
        }
        case PaymentPlansActionTypes.UPDATE_PAYMENT_PLANS_FILTERS: {
            return { ...state, filters: action.payload };
        }

        case PaymentPlansActionTypes.UPDATE_PAYMENT_PLANS_SORT_BY_AND_STATE: {
            return {
                ...state,
                sortBy: action.payload.sortBy,
                sortAscending: action.payload.sortAscending,
            };
        }

        case PaymentPlansActionTypes.UPDATE_PAYMENT_PLANS_TABLE_FILTER_STATE: {
            return {
                ...state,
                tableFilter: action.payload,
            };
        }

        case PaymentPlansActionTypes.CLEAR_PAYMENT_PLANS_STATE_ALL_TABLE_FILTERS: {
            return {
                ...state,
                filters: initialState.filters,
                sortBy: initialState.sortBy,
                sortAscending: initialState.sortAscending,
                tableFilter: initialState.tableFilter,
            };
        }

        case PaymentPlansActionTypes.CLEAR_PAYMENT_PLANS_STATE_DATA: {
            return {
                ...initialState,
            };
        }

        //Single Record
        case PaymentPlansActionTypes.SET_PAYMENT_PLAN_SELECTED_ID_REQUEST: {
            const newActiveData = update(state.activeData, {
                $merge: {
                    selectedId: initialState.activeData.selectedId,
                },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        case PaymentPlansActionTypes.SET_PAYMENT_PLAN_SELECTED_ID_SUCCESS: {
            const newActiveData = update(state.activeData, {
                $merge: {
                    selectedId: action.payload,
                },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        case PaymentPlansActionTypes.GET_PAYMENT_PLAN_DATA_REQUEST: {
            const newActiveData = update(state.activeData, {
                record: { $set: initialState.activeData.record },
                loading: { $set: true },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        case PaymentPlansActionTypes.GET_PAYMENT_PLAN_DATA_SUCCESS: {
            const newActiveData = update(state.activeData, {
                record: { $set: action.payload.record },
                loading: { $set: false },
                errorMessages: { $set: initialState.activeData.errorMessages },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        case PaymentPlansActionTypes.GET_PAYMENT_PLAN_DATA_ERROR: {
            const newActiveData = update(state.activeData, {
                record: { $set: initialState.activeData.record },
                loading: { $set: false },
                errorMessages: { $set: action.payload },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        case PaymentPlansActionTypes.CLEAR_PAYMENT_PLAN_DATA_SUCCESS: {
            return {
                ...state,
                activeData: initialState.activeData,
            };
        }

        // For invoices list reducers
        case PaymentPlansActionTypes.GET_PAYMENT_PLAN_INVOICES_REQUEST: {
            const newInvoicesState = update(state.activeData.invoices, {
                $merge: {
                    loading: true,
                },
            });

            const newActiveData = update(state.activeData, {
                invoices: { $set: newInvoicesState },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        case PaymentPlansActionTypes.GET_PAYMENT_PLAN_INVOICES_SUCCESS: {
            let newDataState = [];
            if (action.payload.pageData.currentPage === 0) {
                newDataState = update(state.activeData.invoices.data, {
                    $set: action.payload.data,
                });
            } else {
                newDataState = uniqWith(
                    concat(state.activeData.invoices.data, action.payload.data),
                    isEqual
                );
            }

            const newInvoicesState = update(state.activeData.invoices, {
                $merge: {
                    loading: false,
                    data: newDataState,
                    pageData: action.payload.pageData,
                    errorMessages:
                        initialState.activeData.invoices.errorMessages,
                },
            });

            const newActiveData = update(state.activeData, {
                invoices: { $set: newInvoicesState },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        case PaymentPlansActionTypes.GET_PAYMENT_PLAN_INVOICES_ERROR: {
            const newInvoicesState = update(state.activeData.invoices, {
                $merge: {
                    loading: false,
                    data: initialState.activeData.invoices.data,
                    errorMessages: action.payload,
                },
            });

            const newActiveData = update(state.activeData, {
                invoices: { $set: newInvoicesState },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        case PaymentPlansActionTypes.UPDATE_PAYMENT_PLAN_INVOICES_FILTERS: {
            const newInvoicesState = update(state.activeData.invoices, {
                $merge: {
                    filters: action.payload,
                },
            });

            const newActiveData = update(state.activeData, {
                invoices: { $set: newInvoicesState },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }
        case PaymentPlansActionTypes.UPDATE_PAYMENT_PLAN_INVOICES_SORT_BY_AND_STATE: {
            const newInvoicesState = update(state.activeData.invoices, {
                $merge: {
                    sortBy: action.payload.sortBy,
                    sortAscending: action.payload.sortAscending,
                    invoiceState: action.payload.invoiceState,
                },
            });

            const newActiveData = update(state.activeData, {
                invoices: { $set: newInvoicesState },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }
        case PaymentPlansActionTypes.UPDATE_PAYMENT_PLAN_INVOICES_TABLE_FILTER_STATE: {
            const newInvoicesState = update(state.activeData.invoices, {
                $merge: {
                    invoiceState: action.payload,
                },
            });

            const newActiveData = update(state.activeData, {
                invoices: { $set: newInvoicesState },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        // For schedule list reducers
        case PaymentPlansActionTypes.GET_PAYMENT_PLAN_DATA_SCHEDULE_REQUEST: {
            const newScheduleState = update(state.activeData.schedule, {
                $merge: {
                    loading: true,
                },
            });

            const newActiveDataState = update(state.activeData, {
                $merge: {
                    schedule: newScheduleState,
                },
            });
            const newDataState = update(state, {
                activeData: {
                    $set: newActiveDataState,
                },
            });

            return {
                ...newDataState,
            };
        }

        case PaymentPlansActionTypes.GET_PAYMENT_PLAN_DATA_SCHEDULE_SUCCESS: {
            let newDataState = [];
            if (action.payload.pageData.currentPage === 0) {
                newDataState = update(state.activeData.schedule.data, {
                    $set: action.payload.data,
                });
            } else {
                newDataState = uniqWith(
                    concat(state.activeData.schedule.data, action.payload.data),
                    isEqual
                );
            }

            const newScheduleState = update(state.activeData.schedule, {
                $merge: {
                    loading: false,
                    data: newDataState,
                    pageData: action.payload.pageData,
                    errorMessages:
                        initialState.activeData.schedule.errorMessages,
                },
            });

            const newActiveDataState = update(state.activeData, {
                $merge: {
                    schedule: newScheduleState,
                },
            });
            const newScheduleDataState = update(state, {
                activeData: {
                    $set: newActiveDataState,
                },
            });

            return {
                ...newScheduleDataState,
            };
        }

        case PaymentPlansActionTypes.GET_PAYMENT_PLAN_DATA_SCHEDULE_ERROR: {
            const newScheduleState = update(state.activeData.schedule, {
                $merge: {
                    loading: false,
                    data: initialState.activeData.schedule.data,
                    errorMessages: action.payload,
                },
            });

            const newActiveDataState = update(state.activeData, {
                $merge: {
                    schedule: newScheduleState,
                },
            });
            const newTicketsState = update(state, {
                activeData: {
                    $set: newActiveDataState,
                },
            });

            return {
                ...newTicketsState,
            };
        }

        case PaymentPlansActionTypes.UPDATE_PAYMENT_PLAN_DATA_SCHEDULE_FILTERS: {
            const newScheduleState = update(state.activeData.schedule, {
                $merge: {
                    filters: action.payload,
                },
            });

            const newActiveDataState = update(state.activeData, {
                $merge: {
                    schedule: newScheduleState,
                },
            });
            const newTicketsState = update(state, {
                activeData: {
                    $set: newActiveDataState,
                },
            });

            return {
                ...newTicketsState,
            };
        }
        case PaymentPlansActionTypes.UPDATE_PAYMENT_PLAN_DATA_SCHEDULE_SORT_BY_AND_STATE: {
            const newScheduleState = update(state.activeData.schedule, {
                $merge: {
                    sortBy: action.payload.sortBy,
                    sortAscending: action.payload.sortAscending,
                },
            });

            const newActiveDataState = update(state.activeData, {
                $merge: {
                    schedule: newScheduleState,
                },
            });
            const newTicketsState = update(state, {
                activeData: {
                    $set: newActiveDataState,
                },
            });

            return {
                ...newTicketsState,
            };
        }

        // For conversation list reducers
        case PaymentPlansActionTypes.GET_PAYMENT_PLAN_CONVERSATION_REQUEST: {
            const newConversationState = update(state.activeData.conversation, {
                $merge: {
                    loading: true,
                },
            });

            const newActiveData = update(state.activeData, {
                conversation: { $set: newConversationState },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        case PaymentPlansActionTypes.GET_PAYMENT_PLAN_CONVERSATION_SUCCESS: {
            let newDataState = [];
            if (action.payload.pageData.currentPage === 0) {
                newDataState = update(state.activeData.conversation.data, {
                    $set: action.payload.data,
                });
            } else {
                newDataState = uniqWith(
                    concat(
                        state.activeData.conversation.data,
                        action.payload.data
                    ),
                    isEqual
                );
            }

            const newConversationState = update(state.activeData.conversation, {
                $merge: {
                    loading: false,
                    data: newDataState,
                    pageData: action.payload.pageData,
                    errorMessages:
                        initialState.activeData.conversation.errorMessages,
                },
            });

            const newActiveData = update(state.activeData, {
                conversation: { $set: newConversationState },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        case PaymentPlansActionTypes.GET_PAYMENT_PLAN_CONVERSATION_ERROR: {
            const newConversationState = update(state.activeData.conversation, {
                $merge: {
                    loading: false,
                    data: initialState.activeData.conversation.data,
                    errorMessages: action.payload,
                },
            });

            const newActiveData = update(state.activeData, {
                conversation: { $set: newConversationState },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        case PaymentPlansActionTypes.UPDATE_PAYMENT_PLAN_CONVERSATION_FILTERS: {
            const newConversationState = update(state.activeData.conversation, {
                $merge: {
                    filters: action.payload,
                },
            });

            const newActiveData = update(state.activeData, {
                conversation: { $set: newConversationState },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        case PaymentPlansActionTypes.UPDATE_PAYMENT_PLAN_CONVERSATION_TABLE_FILTER: {
            const newConversationState = update(state.activeData.conversation, {
                $merge: {
                    conversationTableFilter: action.payload,
                },
            });

            const newActiveData = update(state.activeData, {
                conversation: { $set: newConversationState },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        case PaymentPlansActionTypes.UPDATE_PAYMENT_PLAN_CONVERSATION_SORT_BY: {
            const newConversationState = update(state.activeData.conversation, {
                $merge: {
                    sortBy: action.payload.sortBy,
                    sortAscending: action.payload.sortAscending,
                },
            });

            const newActiveData = update(state.activeData, {
                conversation: { $set: newConversationState },
            });

            return {
                ...state,
                activeData: newActiveData,
            };
        }

        default: {
            return state;
        }
    }
};

// Instead of using default export, we use named exports. That way we can group these exports
// inside the `index.js` folder.
export { reducer as paymentPlansReducer };
